import { useEffect, useState, Fragment } from 'react';
import { KEYBINDINGS, matchKeybinding } from './keybindings';
import { useNavSend } from '../navigation/NavContext';

/**
 * KeyboardHelp — Plan 07-03 Task 2 (FALL-04 + UI-05 closure).
 *
 * Global keybinding dispatcher + ? help overlay.
 *
 * Architecture:
 *   - One window keydown listener on mount, removed on unmount.
 *   - Dispatches to KEYBINDINGS[*].action for bound keys.
 *   - '?' is special-cased: toggles the overlay's useState rather than firing
 *     the binding's (no-op) action.
 *   - Escape handling is Pitfall 4 structural:
 *       • When document.pointerLockElement is non-null, DO NOTHING. The
 *         browser's native Pointer Lock API consumes Escape to release the
 *         lock — we must not preventDefault it (SC 2.1.2 No Keyboard Trap).
 *       • Otherwise, if the overlay is open, close it.
 *   - NO preventDefault is called on any key. Ever.
 *
 * Rendering: the overlay is a `role="dialog"` div fixed at the viewport
 * center, shown only when `show === true`. It is NOT modal (aria-modal="false")
 * because the 3D scene remains interactive behind it — modal behavior would
 * conflict with the goal of letting the user peek at controls without losing
 * the scene.
 *
 * Mount site: UIShell (sibling of the R3F <Canvas>). Do NOT mount inside the
 * Canvas — the dispatcher listens on window, and the overlay is DOM chrome.
 */

// AESTHETIC-BRIEF Rule 7 palette — matches SourcesPanel + EraToggle + ReadTheTour.
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const OCHRE = '#D4A87E';
const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: CREAM,
  color: ESPRESSO,
  padding: '1.5rem 2rem',
  border: `1px solid ${OCHRE}`,
  borderRadius: 2,
  zIndex: 150,
  maxWidth: 480,
  fontFamily: SERIF_STACK,
  boxShadow: '0 4px 16px rgba(59,47,47,0.15)',
  lineHeight: 1.55,
};

const DL_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: '0.5rem 1rem',
  margin: '1rem 0 0',
};

const DT_STYLE: React.CSSProperties = {
  fontFamily: 'monospace',
  fontWeight: 600,
  fontSize: '0.95rem',
};

const DD_STYLE: React.CSSProperties = {
  margin: 0,
  fontSize: '1rem',
};

const FOOTER_STYLE: React.CSSProperties = {
  fontSize: '0.9em',
  opacity: 0.8,
  marginTop: '1rem',
};

export function KeyboardHelp() {
  const [show, setShow] = useState(false);
  const send = useNavSend();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape: honor the native Pointer Lock API contract (Pitfall 4).
      // Never call preventDefault. Only close overlay when not in lock.
      if (e.key === 'Escape') {
        if (!document.pointerLockElement) {
          setShow((prev) => (prev ? false : prev));
        }
        return;
      }

      const binding = matchKeybinding(e);
      if (!binding) return;

      if (binding.key === '?') {
        setShow((prev) => !prev);
        return;
      }

      binding.action(send);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [send]);

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="kb-help-title"
      aria-modal="false"
      style={OVERLAY_STYLE}
    >
      <h2 id="kb-help-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: 500 }}>
        Keyboard Controls
      </h2>
      <dl style={DL_STYLE}>
        {KEYBINDINGS.map((b) => (
          <Fragment key={b.key}>
            <dt style={DT_STYLE}>{b.label}</dt>
            <dd style={DD_STYLE}>{b.description}</dd>
          </Fragment>
        ))}
        <Fragment key="esc">
          <dt style={DT_STYLE}>Esc</dt>
          <dd style={DD_STYLE}>
            Exit pointer-lock (closes this overlay when pointer-lock is not active)
          </dd>
        </Fragment>
      </dl>
      <p style={FOOTER_STYLE}>Press ? or Esc to close.</p>
    </div>
  );
}
